import { Token } from "./BuildTokens";
import { FontMetrics } from "./CanvasKerningApplier";

export type MeasuredToken = {
  token: Token;
  rect: DOMRect;
};

function splitTokensByBr(tokens: Token[]): Token[][] {
  const result: Token[][] = [];
  let chunk: Token[] = [];
  for (const token of tokens) {
    if (token.type === "br") {
      if (chunk.length) result.push(chunk);
      result.push([token]);
      chunk = [];
    } else {
      chunk.push(token);
    }
  }
  if (chunk.length) result.push(chunk);
  return result;
}

export function LayoutMeasurer(
  tokens: Token[],
  container: HTMLElement,
  fontMetrics: FontMetrics
): MeasuredToken[] {
  const wrapper = document.createElement("div");
  const computed = window.getComputedStyle(container);

  wrapper.style.position = "absolute";
  wrapper.style.visibility = "hidden";
  wrapper.style.pointerEvents = "none";
  wrapper.style.width = container.clientWidth + "px";
  wrapper.style.padding = computed.padding;
  wrapper.style.font = computed.font;
  wrapper.style.letterSpacing = computed.letterSpacing;
  wrapper.style.lineHeight = computed.lineHeight;
  wrapper.style.fontVariant = computed.fontVariant;
  wrapper.style.fontStretch = computed.fontStretch;
  wrapper.style.wordBreak = computed.wordBreak;
  wrapper.style.wordWrap = computed.wordWrap;
  wrapper.style.whiteSpace = computed.whiteSpace;

  const spaceMeasurer = document.createElement("span");
  spaceMeasurer.textContent = "\u00a0";
  wrapper.appendChild(spaceMeasurer);
  const spaceWidth = spaceMeasurer.getBoundingClientRect().width;
  wrapper.removeChild(spaceMeasurer);
  wrapper.style.width = container.clientWidth + spaceWidth + "px";
  wrapper.style.boxSizing = "border-box";

  container.appendChild(wrapper);

  const measured: MeasuredToken[] = [];
  const chunks = splitTokensByBr(tokens);

  let isAfterElement = false;
  let lastTokenWasSpace = false;
  let prevMeasuredWasText = false;

  for (const chunk of chunks) {
    if (chunk.length === 1 && chunk[0].type === "br") {
      measured.push({ token: chunk[0], rect: new DOMRect(0, 0, 0, 0) });
      continue;
    }
    if (chunk.length === 0) continue;

    wrapper.innerHTML = "";

    const wordSpans: HTMLSpanElement[] = [];
    const tokenInfo: Array<{
      token: Token;
      wordIndex?: number;
      hadLeadingSpace?: boolean;
      hadTrailingSpace?: boolean;
    }> = [];

    chunk.forEach((token) => {
      switch (token.type) {
        case "text": {
          const raw = token.content;
          const hadLeadingSpace = /^\s/.test(raw);
          const hadTrailingSpace = /\s$/.test(raw);

          const words = raw
            .trim()
            .split(/\s+/)
            .filter((w) => w.length > 0);

          words.forEach((word, wIdx) => {
            const span = document.createElement("span");
            span.style.display = "inline-block";
            span.textContent = word;
            wrapper.appendChild(span);

            if (wIdx < words.length - 1 || hadTrailingSpace) {
              wrapper.appendChild(document.createTextNode(" "));
            }

            wordSpans.push(span);
            tokenInfo.push({
              token: {
                type: "text",
                id: "",
                node: token.node,
                content: word,
                meta: {
                  ...(token.meta || {}),
                  joinPrev:
                    wIdx === 0
                      ? !hadLeadingSpace && !lastTokenWasSpace && prevMeasuredWasText
                      : false,
                },
              },
              wordIndex: wordSpans.length - 1,
              hadLeadingSpace,
              hadTrailingSpace,
            });
          });

          lastTokenWasSpace = hadTrailingSpace;
          prevMeasuredWasText = true;
          break;
        }

        case "element": {
          const node = token.node.cloneNode(true);
          const span = document.createElement("span");
          span.style.display = "inline-block";
          span.appendChild(node);
          wrapper.appendChild(span);
          wordSpans.push(span);
          tokenInfo.push({ token, wordIndex: wordSpans.length - 1 });
          isAfterElement = true;
          prevMeasuredWasText = false;
          lastTokenWasSpace = false;
          break;
        }

        case "space":
          lastTokenWasSpace = true;
          break;

        case "other":
          prevMeasuredWasText = false;
          lastTokenWasSpace = false;
          break;
      }
    });

    wrapper.offsetHeight;

    tokenInfo.forEach((info, idx) => {
      if (info.wordIndex !== undefined) {
        const span = wordSpans[info.wordIndex];
        const domRect = span.getBoundingClientRect();

        let rect: DOMRect;
        if (info.token.type === "text") {
          const accurateWidth = fontMetrics.measureWord(info.token.content);
          rect = new DOMRect(domRect.x, domRect.y, accurateWidth, domRect.height);
        } else {
          rect = domRect;
        }

        const measuredToken: MeasuredToken = {
          token: info.token,
          rect: rect,
        };

        if (isAfterElement && info.token.type === "text") {
          isAfterElement = false;
          measuredToken.token.meta = {
            ...(measuredToken.token.meta || {}),
            isAfterElement: true,
          };
        }

        measured.push(measuredToken);

        if (info.token.type === "element") {
          const prev = measured[measured.length - 2];
          if (prev?.token.type === "text") {
            prev.token.meta = { ...(prev.token.meta || {}), isBeforeElement: true };
          }
        }
      }
    });
  }

  container.removeChild(wrapper);
  return measured;
}
